const ORGANISATION_OPTIONS = [
  'Manakkal Agro Pharma',
  'Manakkal Drugs India',
  'MCCI Patients C/O',
  'Manakkal Veterinary Pharma'
]

const ORGANISATION_ID_TO_NAME = {
  1: 'Manakkal Agro Pharma',
  2: 'Manakkal Drugs India',
  3: 'MCCI Patients C/O',
  4: 'Manakkal Veterinary Pharma'
}

const ORGANISATION_NAME_TO_ID = {
  'Manakkal Agro Pharma': 1,
  'Manakkal Drugs India': 2,
  'MCCI Patients C/O': 3,
  'Manakkal Veterinary Pharma': 4
}

const DEPARTMENT_OPTIONS = [
  'None',
  'Kerala',
  'Tamil Nadu',
  'Karnataka',
  'Hindi',
  'Customer Care',
  'Kerala-Shuhaib'
]

const DEPARTMENT_ID_TO_NAME = {
  1: 'Kerala',
  2: 'Tamil Nadu',
  3: 'Karnataka',
  4: 'Hindi',
  5: 'Customer Care',
  6: 'Kerala-Shuhaib'
}

const DEPARTMENT_NAME_TO_ID = {
  None: null,
  Kerala: 1,
  'Tamil Nadu': 2,
  Karnataka: 3,
  Hindi: 4,
  'Customer Care': 5,
  'Kerala-Shuhaib': 6
}

const ROLE_OPTIONS = [
  'None',
  'superadmin',
  'admin',
  'hr_manager',
  'finance_manager',
  'internal_auditor',
  'senior_accountant',
  'senior_accountant_2',
  'telesales',
  'packer',
  'field_staff',
  'storekeeper',
  'moderator',
  'agent',
  'sales_supervisor',
  'junior_accountant',
  'accounts_trainee',
  'crm',
  'ecom_manager',
  'accountant',
  'marketing',
  'sales_admin',
  'biller'
]

function asArray (value) {
  return Array.isArray(value) ? value : []
}

let userCache = []

function firstDefined (...values) {
  for (const value of values) {
    if (value !== null && value !== undefined && value !== '') {
      return value
    }
  }
  return ''
}

function canonicalizeAgainst (value, options) {
  const trimmed = String(value == null ? '' : value).trim()
  if (!trimmed) return ''
  const lower = trimmed.toLowerCase()
  const match = options.find((opt) => String(opt).trim().toLowerCase() === lower)
  return match || trimmed
}

function normalizeOrganisationLabel (user) {
  let organisation = firstDefined(
    user?.organisation?.name,
    user?.organisation?.title,
    user?.organisation_name,
    user?.organisation,
    ORGANISATION_ID_TO_NAME[user?.organisation_id]
  )

  if (organisation && typeof organisation === 'object') {
    organisation = firstDefined(organisation.name, organisation.title, organisation.label)
  }

  return canonicalizeAgainst(organisation, ORGANISATION_OPTIONS) || 'Manakkal Agro Pharma'
}

function normalizeDepartmentLabel (user) {
  let department = firstDefined(
    user?.department?.name,
    user?.department_name,
    user?.department,
    DEPARTMENT_ID_TO_NAME[user?.department_id]
  )

  if (department && typeof department === 'object') {
    department = firstDefined(department.name, department.title, department.label)
  }

  return canonicalizeAgainst(department, DEPARTMENT_OPTIONS) || 'None'
}

function normalizeRoleLabel (user) {
  const role = firstDefined(
    asArray(user?.roles)[0]?.name,
    user?.role?.name,
    user?.current_role,
    user?.role
  )

  if (!role) {
    return 'None'
  }

  if (typeof role === 'object' && role !== null) {
    return firstDefined(role.name, role.title, role.label)
  }

  return String(role)
}

export function normalizeUsersResponse (payload) {
  if (Array.isArray(payload)) {
    return {
      users: payload,
      you: null
    }
  }

  if (payload && typeof payload === 'object') {
    if (Array.isArray(payload.users)) {
      return {
        users: payload.users,
        you: payload.you ?? null
      }
    }

    if (Array.isArray(payload.data)) {
      return {
        users: payload.data,
        you: payload.you ?? null
      }
    }
  }

  return {
    users: [],
    you: null
  }
}

export function normalizeUserDetailResponse (payload) {
  if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
    return payload
  }

  return {}
}

export function normalizeUserRecord (user, index = 0) {
  const rawStatus = firstDefined(user?.employment_status, user?.status, user?.account_status, 'Active')
  const status = String(rawStatus).toLowerCase() === 'inactive' ? 'Inactive' : 'Active'
  return {
    ...user,
    id: user?.id ?? index + 1,
    name: firstDefined(user?.name, user?.username, user?.full_name, `User ${index + 1}`),
    email: firstDefined(user?.email, ''),
    organisation: normalizeOrganisationLabel(user),
    department: normalizeDepartmentLabel(user),
    role: normalizeRoleLabel(user),
    status
  }
}

export function buildUserRows (users) {
  return asArray(users).map((user, index) => normalizeUserRecord(user, index))
}

export function setUserCache (users) {
  userCache = buildUserRows(users).map((user) => ({ ...user }))
}

export function getUserCache () {
  return userCache.map((user) => ({ ...user }))
}

export function getCachedUserById (id) {
  return userCache.find((user) => String(user.id) === String(id)) || null
}

export function mergeWithUserCache (users) {
  const currentRows = buildUserRows(users)

  if (!userCache.length) {
    return currentRows
  }

  return currentRows.map((row) => {
    const cached = getCachedUserById(row.id)
    // Fresh server data is authoritative — spread it LAST so that server-owned
    // fields (role, organisation, department, status, name, email) always win.
    // The cache only supplements keys the list response happens to omit; it must
    // never override a value the server just returned, otherwise a stale cache
    // masks a role change saved elsewhere (the reverting-role glitch).
    return cached ? { ...cached, ...row } : row
  })
}

export function upsertCachedUser (id, patch) {
  const userId = String(id)
  const cachedIndex = userCache.findIndex((user) => String(user.id) === userId)

  if (cachedIndex === -1) {
    userCache = [
      ...userCache,
      {
        id,
        ...patch
      }
    ]
    return getCachedUserById(id)
  }

  userCache[cachedIndex] = {
    ...userCache[cachedIndex],
    ...patch,
    id: userCache[cachedIndex].id
  }

  return getCachedUserById(id)
}

export const STAFF_FIELD_KEYS = [
  'phone',
  'alternate_phone',
  'address',
  'city',
  'state',
  'pincode',
  'country',
  'date_of_birth',
  'gender',
  'blood_group',
  'marital_status',
  'emergency_contact_name',
  'emergency_contact_phone',
  'emergency_contact_relation',
  'employee_code',
  'biometric_id',
  'designation',
  'date_of_joining',
  'employment_type',
  'employment_status',
  'pan_number',
  'aadhaar_number',
  'bank_account_number',
  'bank_name',
  'bank_ifsc',
  'notes'
]

function pickDateOnly (value) {
  if (!value) return ''
  // The API returns Y-m-d already (per the cast on the model), but tolerate
  // ISO timestamps just in case other callers reach this helper.
  const str = String(value)
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str
  const match = str.match(/^(\d{4}-\d{2}-\d{2})/)
  return match ? match[1] : ''
}

export function buildUserDetailForm (user) {
  const normalized = normalizeUserDetailResponse(user)
  const extras = {}
  STAFF_FIELD_KEYS.forEach((key) => {
    if (key === 'date_of_birth' || key === 'date_of_joining') {
      extras[key] = pickDateOnly(normalized[key])
    } else {
      extras[key] = normalized[key] ?? ''
    }
  })

  return {
    name: firstDefined(normalized.name, normalized.username, ''),
    email: firstDefined(normalized.email, ''),
    organisation: normalizeOrganisationLabel(normalized),
    department: normalizeDepartmentLabel(normalized),
    role: normalizeRoleLabel(normalized),
    organisationId: normalized.organisation_id ?? ORGANISATION_NAME_TO_ID[normalizeOrganisationLabel(normalized)] ?? null,
    departmentId: normalized.department_id ?? DEPARTMENT_NAME_TO_ID[normalizeDepartmentLabel(normalized)] ?? null,
    // branch + state are GLOBAL (independent of org). branch carries its
    // state via FK, so stateId is derived from the branch row after the
    // branch list arrives — left null here on initial hydrate.
    branchId: normalized.branch_id ?? normalized.branch?.id ?? null,
    stateId: normalized.branch?.state_id ?? normalized.branch?.state?.id ?? null,
    ...extras
  }
}

export function buildUserUpdatePayload (form) {
  const payload = {
    _method: 'PUT',
    name: form.name?.trim() ?? '',
    email: form.email?.trim() ?? ''
  }

  const organisationId = ORGANISATION_NAME_TO_ID[form.organisation] ?? form.organisationId ?? null
  const departmentId = DEPARTMENT_NAME_TO_ID[form.department] ?? form.departmentId ?? null

  if (form.role && form.role !== 'None') {
    payload.role = form.role
  }

  if (organisationId !== null && organisationId !== undefined) {
    payload.organisation_id = organisationId
  }

  if (departmentId !== null && departmentId !== undefined) {
    payload.department_id = departmentId
  }

  // branch_id is the new geographic tag. Send null explicitly when cleared
  // so the server can unset the column; omit only if the form never carried
  // the key at all.
  if ('branchId' in form) {
    payload.branch_id = form.branchId === '' || form.branchId === undefined
      ? null
      : form.branchId
  }

  STAFF_FIELD_KEYS.forEach((key) => {
    if (!(key in form)) return
    let value = form[key]
    if (typeof value === 'string') value = value.trim()
    if (key === 'pan_number' && value) value = String(value).toUpperCase()
    if (key === 'bank_ifsc' && value) value = String(value).toUpperCase()
    payload[key] = value === '' || value === undefined ? null : value
  })

  return payload
}

export function buildPasswordPayload (form) {
  const payload = {
    _method: 'PUT',
    password: form.password?.trim() ?? '',
    password_confirmation: form.passwordConfirmation?.trim() ?? ''
  }

  return payload
}

export {
  ORGANISATION_OPTIONS,
  DEPARTMENT_OPTIONS,
  ROLE_OPTIONS,
  ORGANISATION_ID_TO_NAME,
  DEPARTMENT_ID_TO_NAME,
  ORGANISATION_NAME_TO_ID,
  DEPARTMENT_NAME_TO_ID
}
